import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControlLabel,
  Switch,
  Grid,
  Collapse,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  RestartAlt as RestartIcon,
  Delete as DeleteIcon,
  DeleteForever as PermanentDeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Rocket as DeployIcon,
  Description as LogsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Apps as AppsIcon,
  CheckCircle as ActiveIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Block as SuspendedIcon,
  Cloud as CloudIcon,
} from "@mui/icons-material";
import { SPACING } from "../../theme/constants";
import { useSnackbar } from "../../components/common/CustomSnackbar";
import * as AdminService from "../../services/Admin";

const statusConfig = {
  ACTIVE: { color: "success", icon: <ActiveIcon fontSize="small" /> },
  RUNNING: { color: "success", icon: <ActiveIcon fontSize="small" /> },
  PENDING: { color: "warning", icon: <PendingIcon fontSize="small" /> },
  DEPLOYING: { color: "info", icon: <PendingIcon fontSize="small" /> },
  STOPPED: { color: "default", icon: <StopIcon fontSize="small" /> },
  FAILED: { color: "error", icon: <ErrorIcon fontSize="small" /> },
  SUSPENDED: { color: "warning", icon: <SuspendedIcon fontSize="small" /> },
  DELETED: { color: "error", icon: <DeleteIcon fontSize="small" /> },
  MAINTENANCE: { color: "info", icon: <RestartIcon fontSize="small" /> },
};

const deploymentTypeConfig = {
  WORDPRESS: { label: "WordPress", color: "#0073e6" },
  DOCKER_IMAGE: { label: "Docker Image", color: "#00c9a7" },
  DOCKER_COMPOSE: { label: "Docker Compose", color: "#ff9800" },
  GIT_DOCKERFILE: { label: "Git Dockerfile", color: "#9c27b0" },
};

const AdminApplications = () => {
  const { notify } = useSnackbar();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [logsDialog, setLogsDialog] = useState({
    open: false,
    logs: "",
    title: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
    severity: "warning",
  });

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await AdminService.getAdminApplications(includeDeleted);
      setApplications(response?.data || []);
    } catch (err) {
      notify.error(
        err.response?.data?.message || "Failed to fetch applications",
      );
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, notify]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleMenuOpen = (event, app) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedApp(app);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedApp(null);
  };

  const handleAction = async (actionFn, appId, successMsg, loadingKey) => {
    setActionLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      await actionFn();
      notify.success(successMsg);
      fetchApplications();
    } catch (err) {
      notify.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Action failed",
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDeploy = (app) => {
    handleMenuClose();
    setConfirmDialog({
      open: true,
      title: "Deploy Application",
      message: `Deploy all services for "${app.name}"? This will start all containers.`,
      severity: "info",
      action: () =>
        handleAction(
          () => AdminService.adminDeployApplication(app.id),
          app.id,
          `Deployment started for ${app.name}`,
          `deploy-${app.id}`,
        ),
    });
  };

  const handleRedeploy = (app) => {
    handleMenuClose();
    setConfirmDialog({
      open: true,
      title: "Redeploy Application",
      message: `Redeploy "${app.name}"? This will tear down and recreate all containers.`,
      severity: "warning",
      action: () =>
        handleAction(
          () => AdminService.adminRedeployApplication(app.id),
          app.id,
          `Redeployment started for ${app.name}`,
          `redeploy-${app.id}`,
        ),
    });
  };

  const handlePermanentDelete = (app) => {
    handleMenuClose();
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Application",
      message: `Permanently delete "${app.name}" and all its services, containers, and data? This cannot be undone.`,
      severity: "error",
      action: () =>
        handleAction(
          () => AdminService.adminPermanentDeleteApplication(app.id),
          app.id,
          `${app.name} permanently deleted`,
          `delete-${app.id}`,
        ),
    });
  };

  const handleRestore = (app) => {
    handleMenuClose();
    handleAction(
      () => AdminService.adminRestoreApplication(app.id),
      app.id,
      `${app.name} restored`,
      `restore-${app.id}`,
    );
  };

  const handleServiceAction = async (app, service, action) => {
    const actionMap = {
      start: { fn: AdminService.adminStartService, msg: "started" },
      stop: { fn: AdminService.adminStopService, msg: "stopped" },
      restart: { fn: AdminService.adminRestartService, msg: "restarted" },
    };
    const { fn, msg } = actionMap[action];
    setActionLoading((prev) => ({
      ...prev,
      [`${action}-${service.id}`]: true,
    }));
    try {
      await fn(app.id, service.id);
      notify.success(`Service ${service.name} ${msg}`);
      fetchApplications();
    } catch (err) {
      notify.error(
        err.response?.data?.message || `Failed to ${action} service`,
      );
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [`${action}-${service.id}`]: false,
      }));
    }
  };

  const handleViewLogs = async (app, service) => {
    try {
      const response = await AdminService.adminGetServiceLogs(
        app.id,
        service.id,
        {
          tail: 200,
        },
      );
      setLogsDialog({
        open: true,
        logs: response?.data?.logs || "No logs available",
        title: `Logs: ${service.name} (${app.name})`,
      });
    } catch (err) {
      notify.error(err.response?.data?.message || "Failed to fetch logs");
    }
  };

  const filteredApps = applications.filter((app) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      app.name?.toLowerCase().includes(query) ||
      app.user?.email?.toLowerCase().includes(query) ||
      app.deploymentType?.toLowerCase().includes(query) ||
      app.id?.toLowerCase().includes(query)
    );
  });

  const stats = {
    total: applications.length,
    active: applications.filter((a) => a.status === "ACTIVE").length,
    pending: applications.filter((a) => a.status === "PENDING").length,
    failed: applications.filter(
      (a) => a.status === "FAILED" || a.status === "SUSPENDED",
    ).length,
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: SPACING.xl,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            Docker Applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all Docker applications, services, and containers across the
            platform
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Show deleted
              </Typography>
            }
          />
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchApplications}
              disabled={loading}
              size="small"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Apps",
            value: stats.total,
            icon: <AppsIcon />,
            color: "primary",
          },
          {
            label: "Active",
            value: stats.active,
            icon: <ActiveIcon />,
            color: "success",
          },
          {
            label: "Pending",
            value: stats.pending,
            icon: <PendingIcon />,
            color: "warning",
          },
          {
            label: "Failed",
            value: stats.failed,
            icon: <ErrorIcon />,
            color: "error",
          },
        ].map((stat) => (
          <Grid key={stat.label} size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${stat.color}.light`,
                      color: `${stat.color}.main`,
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search applications by name, email, type, or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Applications Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredApps.length === 0 ? (
          <CardContent>
            <Box sx={{ textAlign: "center", py: 6 }}>
              <AppsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                {searchQuery
                  ? "No applications match your search"
                  : "No applications found"}
              </Typography>
            </Box>
          </CardContent>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Application</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Worker</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApps.map((app) => (
                  <React.Fragment key={app.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        opacity: app.deletedAt ? 0.6 : 1,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={() =>
                        setExpandedApp(expandedApp === app.id ? null : app.id)
                      }
                    >
                      <TableCell sx={{ pr: 0 }}>
                        <IconButton size="small">
                          {expandedApp === app.id ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {app.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {app.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            deploymentTypeConfig[app.deploymentType]?.label ||
                            app.deploymentType
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              deploymentTypeConfig[app.deploymentType]?.color +
                              "18",
                            color:
                              deploymentTypeConfig[app.deploymentType]?.color,
                            fontWeight: 500,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig[app.status]?.icon}
                          label={app.status}
                          size="small"
                          color={statusConfig[app.status]?.color || "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${app.services?.length || 0} services`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.8125rem">
                          {app.user?.email || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.8125rem">
                          {app.worker?.name || "Unassigned"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(app.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, app)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Services Row */}
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{
                          py: 0,
                          borderBottom: expandedApp === app.id ? 1 : 0,
                        }}
                      >
                        <Collapse
                          in={expandedApp === app.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1.5, fontWeight: 600 }}
                            >
                              Services ({app.services?.length || 0})
                            </Typography>
                            {app.services?.length > 0 ? (
                              <Table
                                size="small"
                                sx={{
                                  bgcolor: "background.default",
                                  borderRadius: 1,
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {app.services.map((service) => (
                                    <TableRow key={service.id}>
                                      <TableCell>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CloudIcon
                                            fontSize="small"
                                            color="action"
                                          />
                                          <Typography
                                            variant="body2"
                                            fontWeight={500}
                                          >
                                            {service.name}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={service.status || "Unknown"}
                                          size="small"
                                          color={
                                            statusConfig[service.status]
                                              ?.color || "default"
                                          }
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                          <Tooltip title="Start">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={() =>
                                                handleServiceAction(
                                                  app,
                                                  service,
                                                  "start",
                                                )
                                              }
                                              disabled={
                                                actionLoading[
                                                  `start-${service.id}`
                                                ]
                                              }
                                            >
                                              {actionLoading[
                                                `start-${service.id}`
                                              ] ? (
                                                <CircularProgress size={14} />
                                              ) : (
                                                <StartIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Stop">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() =>
                                                handleServiceAction(
                                                  app,
                                                  service,
                                                  "stop",
                                                )
                                              }
                                              disabled={
                                                actionLoading[
                                                  `stop-${service.id}`
                                                ]
                                              }
                                            >
                                              {actionLoading[
                                                `stop-${service.id}`
                                              ] ? (
                                                <CircularProgress size={14} />
                                              ) : (
                                                <StopIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Restart">
                                            <IconButton
                                              size="small"
                                              color="info"
                                              onClick={() =>
                                                handleServiceAction(
                                                  app,
                                                  service,
                                                  "restart",
                                                )
                                              }
                                              disabled={
                                                actionLoading[
                                                  `restart-${service.id}`
                                                ]
                                              }
                                            >
                                              {actionLoading[
                                                `restart-${service.id}`
                                              ] ? (
                                                <CircularProgress size={14} />
                                              ) : (
                                                <RestartIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="View Logs">
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleViewLogs(app, service)
                                              }
                                            >
                                              <LogsIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No services configured for this application.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedApp && !selectedApp.deletedAt && (
          <>
            <MenuItem onClick={() => handleDeploy(selectedApp)}>
              <ListItemIcon>
                <DeployIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Deploy</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleRedeploy(selectedApp)}>
              <ListItemIcon>
                <RestartIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText>Redeploy</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleAction(
                  () =>
                    AdminService.adminPermanentDeleteApplication(
                      selectedApp.id,
                    ),
                  selectedApp.id,
                  `${selectedApp.name} permanently deleted`,
                  `delete-${selectedApp.id}`,
                );
              }}
            >
              <ListItemIcon>
                <PermanentDeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>
                Permanently Delete
              </ListItemText>
            </MenuItem>
          </>
        )}
        {selectedApp?.deletedAt && (
          <>
            <MenuItem onClick={() => handleRestore(selectedApp)}>
              <ListItemIcon>
                <RestoreIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Restore</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handlePermanentDelete(selectedApp)}>
              <ListItemIcon>
                <PermanentDeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>
                Permanently Delete
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmDialog.message}</Typography>
          {confirmDialog.severity === "error" && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setConfirmDialog((prev) => ({ ...prev, open: false }))
            }
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.severity === "error" ? "error" : "primary"}
            size="small"
            onClick={() => {
              confirmDialog.action?.();
              setConfirmDialog((prev) => ({ ...prev, open: false }));
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog
        open={logsDialog.open}
        onClose={() => setLogsDialog({ open: false, logs: "", title: "" })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{logsDialog.title}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              bgcolor: "grey.900",
              color: "grey.100",
              borderRadius: 1,
              p: 2,
              fontFamily: "monospace",
              fontSize: "0.75rem",
              lineHeight: 1.6,
              maxHeight: 480,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {logsDialog.logs}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLogsDialog({ open: false, logs: "", title: "" })}
            size="small"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminApplications;
